import {useEffect, useState} from "react";
import i18n from "../i18n";
import {useTranslation} from "react-i18next";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import {GlobalFeatureFlagsCard} from "../components/feature-flags/GlobalFeatureFlagsCard";
import {TenantFeatureFlagsTable} from "../components/feature-flags/TenantFeatureFlagsTable";
import {PageHeader} from "../components/ui/PageHeader";
import {getGlobalFeatureFlags} from "../api/configurationApi";
import {useAppSelector} from "../store/hooks";
import {tenantsSelectors} from "../store/tenantsSlice";
import type {GlobalFeatureFlags} from "../types/configuration";

type Notice = {severity: "success" | "error"; message: string};

export function FeatureFlagsPage() {
    const {t} = useTranslation();
    const tenants = useAppSelector(tenantsSelectors.selectAll);
    const [globalFlags, setGlobalFlags] = useState<GlobalFeatureFlags | null>(null);
    const [notice, setNotice] = useState<Notice | null>(null);

    useEffect(() => {
        document.title = t("FLAGS_PAGE_TITLE");
    }, [t]);

    // The global defaults are read once here: the card edits them and the matrix shows what inherited cells resolve to.
    useEffect(() => {
        let active = true;
        getGlobalFeatureFlags()
                .then((flags) => {
                    if (active) setGlobalFlags(flags);
                })
                .catch((e: unknown) => {
                    if (active) setNotice({severity: "error", message: e instanceof Error ? e.message : i18n.t("FLAGS_LOAD_GLOBAL_ERROR")});
                });
        return () => {
            active = false;
        };
    }, []);

    const showError = (message: string) => setNotice({severity: "error", message});

    return (
            <>
                <PageHeader title={t("NAV_FEATURE_FLAGS")} subtitle={t("FLAGS_PAGE_SUBTITLE")} />
                <GlobalFeatureFlagsCard flags={globalFlags} tenants={tenants} onSaved={setGlobalFlags} onError={showError} />
                <TenantFeatureFlagsTable
                        globalFlags={globalFlags}
                        onError={showError}
                        onSaved={(message) => setNotice({severity: "success", message})}
                />
                <Snackbar
                        open={notice !== null}
                        autoHideDuration={notice?.severity === "success" ? 3000 : 6000}
                        onClose={() => setNotice(null)}
                        anchorOrigin={{vertical: "bottom", horizontal: "center"}}
                >
                    <Alert severity={notice?.severity ?? "success"} onClose={() => setNotice(null)} variant="filled">
                        {notice?.message}
                    </Alert>
                </Snackbar>
            </>
    );
}
